import { validUrl } from "../markup/shared"
import { findSlotMarkers, isWholeSlotMarker } from "../model/slots"
import type {
  ParsedMessage,
  ParsedMessageEntity,
  SlotOccurrence,
  VisualEdit,
} from "../types"
import type { QuillPresentation } from "./quillAdapter"
import {
  displayRangeToSource,
  toSourceRuns,
  type EditorRange,
} from "./sourceTokens"

export interface EditorSlot {
  id: string
  label: string
  occurrences: SlotOccurrence[]
}

/** Read-only presentation of internal markers. Literal UUIDs are user content;
 * editable fields must use literal/token runs instead of this projection. */
export function safeDisplay(value: string): string {
  return toSourceRuns(value)
    .map((run) => (run.kind === "text" ? run.text : "Ссылка"))
    .join("")
}

export function occurrenceLocation(occurrence: SlotOccurrence): string {
  if (occurrence.document.startsWith("button")) {
    const [, , row, button] = occurrence.path.split("/")
    return `кнопка ${Number(row) + 1}.${Number(button) + 1}`
  }
  return occurrence.document === "entity-url"
    ? `аннотация ${Number(occurrence.path.split("/")[3]) + 1}`
    : `текст, позиция ${(occurrence.offset ?? 0) + 1}`
}

export interface LinkSelection {
  range: EditorRange
  text: string
  url: string
  entityIndex?: number
  entity?:
    | Extract<ParsedMessageEntity, { type: "text_link" }>
    | (ParsedMessageEntity & { type: "url" })
}

/** Conservative semantic-text discovery, not auto-link formatting. Native URL
 * annotations remain authoritative; only inferred spans trim prose punctuation. */
function bareUrlAt(text: string, index: number): LinkSelection | undefined {
  for (const match of text.matchAll(/https?:\/\/[^\s<>"'«»“”‘’]+/gi)) {
    const start = match.index!
    if (start && !/[\s([{"'«“‘<]/.test(text[start - 1])) continue
    const candidate = match[0]
    if (index < start || index >= start + candidate.length) continue
    const opening: Record<string, string> = { ")": "(", "]": "[", "}": "{" }
    const unmatched = Object.fromEntries(
      Object.entries(opening).map(([close, open]) => [
        close,
        candidate.split(close).length - candidate.split(open).length,
      ]),
    )
    let length = candidate.length
    while (length) {
      const end = candidate[length - 1]
      if (/[.,!?;:…。，！？；：]/.test(end) || unmatched[end] > 0) {
        if (opening[end]) unmatched[end]--
        length--
      } else break
    }
    const url = candidate.slice(0, length)
    if (index >= start && index < start + url.length && validUrl(url))
      return {
        range: { index: start, length: url.length },
        text: "",
        url,
      }
  }
}

/** Source syntax/destinations map to semantic ranges, not DOM text offsets. */
export function semanticSelection(
  selection: EditorRange,
  presentation: QuillPresentation,
  parsed: ParsedMessage,
): EditorRange {
  const range = displayRangeToSource(selection, presentation.mapping)
  if (presentation.parsed || parsed.formatting.kind !== "markup") return range
  const endpoint = (position: number, end: boolean) => {
    const mapping = parsed.mapping.find(
      (entry) =>
        position >= entry.sourceRange.offset &&
        position < entry.sourceRange.offset + entry.sourceRange.length,
    )
    if (!mapping) return parsed.text.length
    const { sourceRange, textRange } = mapping
    if (mapping.kind === "text" && sourceRange.length === textRange.length)
      return textRange.offset + position - sourceRange.offset
    return textRange.offset + (end ? textRange.length : 0)
  }
  const index = endpoint(range.index, false)
  return {
    index,
    length: range.length
      ? Math.max(0, endpoint(range.index + range.length, true) - index)
      : 0,
  }
}

export function findLinkSelection(
  parsed: ParsedMessage,
  range: EditorRange,
): LinkSelection {
  const entityIndex = parsed.entities.findIndex(
    (entity) =>
      entity.type === "text_link" &&
      range.index >= entity.offset &&
      range.index < entity.offset + entity.length,
  )
  const entity = parsed.entities[entityIndex]
  if (entity?.type === "text_link")
    return {
      range: { index: entity.offset, length: entity.length },
      text: parsed.text.slice(entity.offset, entity.offset + entity.length),
      url: entity.url,
      entityIndex,
      entity,
    }
  const marker = findSlotMarkers(parsed.text).find(
    (item) =>
      range.index >= item.offset && range.index < item.offset + item.length,
  )
  if (marker)
    return {
      range: { index: marker.offset, length: marker.length },
      text: "",
      url: parsed.text.slice(marker.offset, marker.offset + marker.length),
    }
  const nativeUrlIndex = parsed.entities.findIndex(
    (item) =>
      item.type === "url" &&
      range.index >= item.offset &&
      range.index < item.offset + item.length,
  )
  const nativeUrl = parsed.entities[nativeUrlIndex]
  if (nativeUrl?.type === "url") {
    const url = parsed.text.slice(
      nativeUrl.offset,
      nativeUrl.offset + nativeUrl.length,
    )
    if (validUrl(url) && !isWholeSlotMarker(url))
      return {
        range: { index: nativeUrl.offset, length: nativeUrl.length },
        text: "",
        url,
        entityIndex: nativeUrlIndex,
        entity: { ...nativeUrl, type: "url" },
      }
    // Do not infer a different URL inside an explicit, non-HTTP(S) annotation.
  } else {
    const bare = bareUrlAt(parsed.text, range.index)
    if (bare) return bare
  }
  return {
    range,
    text: parsed.text.slice(range.index, range.index + range.length),
    url: "",
  }
}

export function linkEdit(
  selection: LinkSelection,
  text: string,
  url: string,
): VisualEdit {
  // A settings visit is not an annotation rewrite (nor an undo boundary).
  if (selection.text === text && selection.url === url)
    return { operations: [] }
  const operations: VisualEdit["operations"] = []
  if (selection.entityIndex !== undefined)
    operations.push({ kind: "removeEntity", index: selection.entityIndex })
  operations.push({
    kind: "text",
    from: selection.range.index,
    to: selection.range.index + selection.range.length,
    text: text || url,
  })
  if (text)
    operations.push({
      kind: "addEntity",
      entity: {
        ...selection.entity,
        type: "text_link",
        offset: selection.range.index,
        length: text.length,
        url,
      },
    })
  else if (selection.entity?.type === "url" && !isWholeSlotMarker(url)) {
    operations.push({
      kind: "addEntity",
      entity: {
        ...selection.entity,
        offset: selection.range.index,
        length: url.length,
      },
    })
  }
  return { operations }
}
